// StepStrip: where a record is in a real sequence (ordered to shipped, asked to arrived), each
// step with who moved it there or what it waits on. Done steps are lit gold with a check, the
// current one holds a ring of accent light, steps not reached are recessed slots waiting to be
// filled. A step that lands while someone is looking runs the line to it, lights, and draws its
// check. Across from `sm`, a vertical list below it, the line running down between markers.
// `numbered` is a wizard's indicator instead: a centred row of numbered circles, no frame.
// docs/operations/design-standard.md, "Controls".

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import FadeIn from './FadeIn';
import ProgressRing from './ProgressRing';
import Reveal from './Reveal';
import { IconCheck, IconClose, IconPause } from './icons';

/** `held` is paused and can resume (on hold); `stopped` ended there (cancelled, won't buy). */
export type StepState = 'done' | 'current' | 'future' | 'held' | 'stopped';

export interface StepItem {
  /** Stable identity, so a step that lands is told from one that was always done. Defaults to its place. */
  key?: string;
  /** What the step is, with its count where it has one: "Built 110 of 110". */
  label: string;
  /** One line under it: who and when, or what it waits on. */
  detail?: string | null;
  state: StepState;
  /** The step's own count as its marker, so the strip and the count can never disagree. */
  progress?: { value: number; max: number };
}

export interface StepStripProps {
  /** Names the sequence: "Order journey". */
  label: string;
  steps: StepItem[];
  /** Under the steps, inside the frame: a line saying where the record and its numbers disagree. */
  children?: React.ReactNode;
  /**
   * `rail` (default): a record's journey in a frame, with details and counts. `numbered`: a
   * wizard's steps (Upload, Review, Confirm) as numbered circles in a centred row, label only.
   */
  variant?: 'rail' | 'numbered';
  className?: string;
}

const SAID: Record<StepState, string> = { done: 'done', current: 'now', future: 'not yet', held: 'on hold here', stopped: 'stopped here' };

const LAND_MS = 900;

const reached = (s: StepState): boolean => s !== 'future';

const MARK = 'flex h-7 w-7 flex-none items-center justify-center rounded-full transition-[background-color,box-shadow,color] duration-standard ease-emphasized';

const Marker: React.FC<{ step: StepItem; drawn: boolean; landing: boolean }> = ({ step, drawn, landing }) => {
  if (step.progress) {
    return (
      <span className="flex h-7 w-7 flex-none items-center justify-center">
        <ProgressRing value={step.progress.value} max={step.progress.max} size={28} success decorative />
      </span>
    );
  }
  switch (step.state) {
    case 'done':
      return (
        // Landing: the line reaches the marker before it lights, and the check draws once it has.
        <span className={`${MARK} bg-primary text-on-primary ${landing ? 'shadow-glow-soft [transition-delay:240ms] [&_path]:[animation-delay:300ms]' : ''}`}>
          <IconCheck draw={drawn} />
        </span>
      );
    case 'current':
      return (
        <span className={`${MARK} bg-surface-container-highest shadow-glow`}>
          <span className="h-2.5 w-2.5 rounded-full bg-primary" />
        </span>
      );
    case 'held':
      return <span className={`${MARK} bg-surface-container-highest text-on-surface-variant`}><IconPause /></span>;
    case 'stopped':
      return <span className={`${MARK} bg-surface-container-highest text-on-surface-variant`}><IconClose className="h-3.5 w-3.5" /></span>;
    default:
      return <span className={`${MARK} bg-surface-container-high shadow-recess`} />;
  }
};

/** The line to the next step: a recessed track that the gold runs along once that step is reached. */
const Connector: React.FC<{ lit: boolean }> = ({ lit }) => (
  <span
    aria-hidden="true"
    className="relative my-1 w-0.5 flex-1 overflow-hidden rounded-inner bg-surface-container-high sm:mx-2 sm:my-0 sm:h-0.5 sm:w-auto"
  >
    <span
      className={`absolute inset-0 origin-top bg-primary/60 transition-[transform,opacity] duration-spring ease-spring motion-reduce:transition-[opacity] sm:origin-left ${
        lit ? 'scale-100 opacity-100' : 'opacity-0 max-sm:scale-y-0 sm:scale-x-0'
      }`}
    />
  </span>
);

const NUM_MARK = 'flex h-8 w-8 flex-none items-center justify-center rounded-full text-sm font-bold tabular-nums transition-[background-color,box-shadow,color] duration-standard ease-emphasized';

/** A numbered step's circle: its number, a check once passed. The list's order already says which. */
const NumberedMarker: React.FC<{ step: StepItem; n: number; drawn: boolean }> = ({ step, n, drawn }) => {
  switch (step.state) {
    case 'done':
      return <span aria-hidden="true" className={`${NUM_MARK} bg-primary text-on-primary`}><IconCheck className="h-4 w-4" draw={drawn} /></span>;
    case 'current':
      return <span aria-hidden="true" className={`${NUM_MARK} bg-primary text-on-primary shadow-glow-soft`}>{n}</span>;
    case 'held':
      return <span aria-hidden="true" className={`${NUM_MARK} bg-surface-container-highest text-on-surface-variant`}><IconPause /></span>;
    case 'stopped':
      return <span aria-hidden="true" className={`${NUM_MARK} bg-surface-container-highest text-on-surface-variant`}><IconClose className="h-3.5 w-3.5" /></span>;
    default:
      return <span aria-hidden="true" className={`${NUM_MARK} bg-surface-container-high text-on-surface-variant shadow-recess`}>{n}</span>;
  }
};

/** 48 by 2 between numbered steps (24 on a phone), the gold running along it once the next is reached. */
const NumberedConnector: React.FC<{ lit: boolean }> = ({ lit }) => (
  <span aria-hidden="true" className="relative h-0.5 w-6 flex-none overflow-hidden rounded-inner bg-surface-container-high sm:w-12">
    <span
      className={`absolute inset-0 origin-left bg-primary transition-[transform,opacity] duration-spring ease-spring motion-reduce:transition-[opacity] ${
        lit ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
      }`}
    />
  </span>
);

export const StepStrip: React.FC<StepStripProps> = ({ label, steps, children, variant = 'rail', className = '' }) => {
  const keyOf = (s: StepItem, i: number) => s.key ?? String(i);
  const signature = steps.map((s, i) => `${keyOf(s, i)}:${s.state}`).join('|');

  // Steps that became done since the last render: only a step that lands while someone is
  // looking draws its check (and keeps the drawn check), and glows for a moment. The first
  // paint lands nothing.
  const prev = useRef<Map<string, StepState> | null>(null);
  const [drawn, setDrawn] = useState<ReadonlySet<string>>(() => new Set());
  const [landing, setLanding] = useState<ReadonlySet<string>>(() => new Set());
  useLayoutEffect(() => {
    const was = prev.current;
    const now = new Map(steps.map((s, i) => [keyOf(s, i), s.state] as [string, StepState]));
    prev.current = now;
    if (!was) return;
    const fresh = Array.from(now.entries())
      .filter(([k, st]) => st === 'done' && was.has(k) && was.get(k) !== 'done')
      .map(([k]) => k);
    if (!fresh.length) return;
    setDrawn((d) => new Set([...Array.from(d), ...fresh]));
    setLanding(new Set(fresh));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
  useEffect(() => {
    if (!landing.size) return undefined;
    const t = window.setTimeout(() => setLanding(new Set()), LAND_MS);
    return () => window.clearTimeout(t);
  }, [landing]);

  if (variant === 'numbered') {
    return (
      <div className={className}>
        <ol role="list" aria-label={label} className="m-0 flex list-none flex-wrap items-center justify-center gap-2 p-0">
          {steps.map((s, i) => {
            const last = i === steps.length - 1;
            return (
              <li
                key={keyOf(s, i)}
                role="listitem"
                aria-current={s.state === 'current' ? 'step' : undefined}
                className="flex min-w-0 items-center gap-2"
              >
                <NumberedMarker step={s} n={i + 1} drawn={drawn.has(keyOf(s, i))} />
                <span
                  className={`whitespace-nowrap text-sm font-medium transition-colors duration-standard ease-emphasized ${
                    reached(s.state) ? 'text-on-surface' : 'text-on-surface-variant'
                  }`}
                >
                  {s.label}
                  <span className="sr-only">, {SAID[s.state]}</span>
                </span>
                {!last && <NumberedConnector lit={reached(steps[i + 1].state)} />}
              </li>
            );
          })}
        </ol>
        <Reveal show={!!children}>
          <div className="pt-3">{children}</div>
        </Reveal>
      </div>
    );
  }

  return (
    <div className={`rounded-panel bg-surface-container-low p-4 ${className}`}>
      <ol
        role="list"
        aria-label={label}
        style={{ '--steps': steps.length } as React.CSSProperties}
        className="m-0 grid list-none grid-cols-1 p-0 sm:grid-cols-[repeat(var(--steps),minmax(0,1fr))]"
      >
        {steps.map((s, i) => {
          const last = i === steps.length - 1;
          return (
            <li
              key={keyOf(s, i)}
              role="listitem"
              aria-current={s.state === 'current' ? 'step' : undefined}
              className="grid min-w-0 grid-cols-[1.75rem_minmax(0,1fr)] gap-x-3 sm:flex sm:flex-col sm:gap-2"
            >
              <div className="flex flex-col items-center sm:w-full sm:flex-row">
                <Marker step={s} drawn={drawn.has(keyOf(s, i))} landing={landing.has(keyOf(s, i))} />
                {!last && <Connector lit={reached(steps[i + 1].state)} />}
              </div>
              <div className={`min-w-0 sm:pb-0 sm:pr-3 ${last ? '' : 'pb-4'}`}>
                <div
                  className={`text-sm font-semibold leading-7 tabular-nums transition-colors duration-standard ease-emphasized sm:leading-5 ${
                    s.state === 'future' ? 'text-on-surface-variant' : 'text-on-surface'
                  }`}
                >
                  {s.label}
                  <span className="sr-only">, {SAID[s.state]}</span>
                </div>
                <Reveal show={!!s.detail}>
                  <FadeIn
                    key={s.detail ?? ''}
                    className={`mt-0.5 break-words text-xs ${
                      s.state === 'current' ? 'text-primary' : 'text-on-surface-variant'
                    }`}
                  >
                    {s.detail}
                  </FadeIn>
                </Reveal>
              </div>
            </li>
          );
        })}
      </ol>
      <Reveal show={!!children}>
        <div className="pt-3">{children}</div>
      </Reveal>
    </div>
  );
};

export default StepStrip;
